import { Box, Tooltip } from "@mui/material";
import {
  DriveFileRenameOutline as RenameIcon,
  UnfoldLessOutlined as CollapseIcon,
} from "@mui/icons-material";
import { useState } from "react";
import { InlineEdit, TextButton } from "../ui/primitives";
import { v2, nums } from "../ui/v2Tokens";
import { energyFigure, energyText, macroFigure, type Figure } from "../ui/nutritionUnits";
import { useNutritionDisplayMode } from "../../../../contexts/NutritionDisplayContext";
import FoodRowCore from "./FoodRowCore";
import FoodSearchField from "./FoodSearchField";
import {
  autoGroupName as autoName,
  matchedToCalories,
  newId,
  subGroupDelta,
  subGroupTotals,
  withGroupItems as withItems,
  withServing,
  type FoodItemV2,
  type SubGroupV2,
} from "../model/nutritionV2Model";

type Props = {
  item: FoodItemV2;
  onChange: (substitutions: SubGroupV2[]) => void;
  /**
   * Fold the panel away. Optional because the panel does not own whether it is open — the food
   * row does — so a caller that renders it some other way simply gets no close button rather
   * than a dead one.
   */
  onClose?: () => void;
  /**
   * Swap an alternative with the food it stands in for. Optional, and the button is simply absent
   * without it: only the caller owning the PRIMARY item can do this — the panel can rewrite
   * `substitutions` and nothing else — so a panel rendered without that owner must not offer it.
   */
  onPromote?: (groupUid: string, subUid: string) => void;
};

/**
 * A signed drift from the original, `+1.2 נק׳`. In points a drift too small to register rounds to
 * zero, and it prints as `+0` rather than as the `-0` a negative zero formats to (in either mode).
 */
const signed = (value: number, figure: (v: number) => Figure): string => {
  const f = figure(value);
  const zero = Number(f.text.replace(/[^\d.]/g, "")) === 0;
  return `${value >= 0 || zero ? "+" : ""}${figure(zero ? 0 : value).text} ${f.unit}`;
};

/**
 * Per-food substitutions ("חלופה"). A substitution is a GROUP — it may hold several
 * foods, so "150g chicken" can be swapped for "2 eggs + a slice of cheese".
 * Substitutions never count towards the daily totals.
 *
 * ── which of the two "add" paths is cheap ──
 *
 * Adding ANOTHER alternative is the common act, and it is one search away: the panel's own
 * search field creates a NEW group per pick and stays ready for the next one. Adding a
 * second food INTO an existing group — "eat both of these instead" — is the rarer, more
 * deliberate one, so it costs a press on that group first, and an unselected group shows no
 * search field at all. The panel used to have it the other way round: every group carried a
 * permanent search box, so five separate alternatives meant five button presses and five
 * fields to aim at, and stacking two foods into one group was the accident that was easy to
 * make.
 */
const SubstitutionsPanel = ({ item, onChange, onClose, onPromote }: Props) => {
  const groups = item.substitutions || [];
  const mode = useNutritionDisplayMode();
  /**
   * The one group currently open for "add another food into this one".
   *
   * `focus` records HOW it was opened: pressing "+ הוסף מוצר" is a request to type, so the
   * field takes focus; pressing the group's body is not, and autofocusing there would rip
   * the caret out of the quantity box the trainer just clicked.
   */
  const [openGroup, setOpenGroup] = useState<{ uid: string; focus: boolean } | null>(null);
  /** The one alternative whose name is open for editing, if any — see `nameEdit`. */
  const [renameGroup, setRenameGroup] = useState<string | null>(null);

  const updateGroup = (groupUid: string, changes: Partial<SubGroupV2>) =>
    onChange(groups.map((g) => (g.uid === groupUid ? { ...g, ...changes } : g)));

  const updateGroupItems = (groupUid: string, items: FoodItemV2[]) =>
    onChange(groups.map((g) => (g.uid === groupUid ? withItems(g, items) : g)));

  /**
   * The panel's primary action: one picked food becomes one NEW alternative.
   *
   * Added already worth the same as the food it replaces. Working out "how many eggs is 150g
   * of chicken" by hand is how alternatives end up 200 kcal adrift — which is exactly what the
   * red delta line on each group has always been reporting.
   */
  const addGroupFromFood = (newItem: FoodItemV2) => {
    const food = matchedToCalories({ ...newItem, order: 0 }, item);
    onChange([...groups, { uid: newId("sub"), name: autoName([food]), items: [food] }]);
    // the trainer went back to the primary field, so the secondary one folds away and the
    // panel is left with exactly one search box open — the one they are typing in
    setOpenGroup(null);
  };

  const removeGroup = (groupUid: string) => onChange(groups.filter((g) => g.uid !== groupUid));

  return (
    <Box
      sx={{
        position: "relative",
        // A substitutions panel sits inside a food row, inside a meal card, inside the page —
        // three levels of padding deep. On a desktop column that indentation is what says "these
        // belong to the food above"; at 375px it is a third of the width, so it shrinks to the
        // narrowest indent that still reads as nested and lets the rail do the rest of the work.
        padding: { xs: "0 8px 8px 6px", md: "0 16px 10px 12px" },
        background: "#F4F7F9",
        borderBottom: `1px solid ${v2.lineSoft}`,
        // the rail that ties the alternatives to the product they replace — without it a
        // swap for the third food reads as a swap for the meal
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          bottom: 0,
          insetInlineEnd: { xs: "12px", md: "26px" },
          width: "2px",
          borderRadius: "2px",
          background: v2.line,
        },
      }}
    >
      <Box sx={{ padding: { xs: "8px 22px 0 4px", md: "9px 34px 0 4px" } }}>
        <Box sx={{ fontSize: "12.5px", fontWeight: 500, color: v2.ink2 }}>
          חלופות ל{item.name}
          <Box component="span" sx={{ fontWeight: 400, color: v2.ink4 }}>
            {" "}— אינן נספרות בסיכום היומי
          </Box>
        </Box>
      </Box>

      {groups.length === 0 && (
        <Box sx={{ padding: { xs: "6px 22px 8px 4px", md: "6px 34px 9px 4px" }, fontSize: "12.5px", color: v2.ink4 }}>
          אין חלופות עדיין — חפשו מוצר בשדה שלמטה כדי ליצור את הראשונה.
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          padding: groups.length ? { xs: "8px 22px 4px 4px", md: "10px 34px 4px 4px" } : 0,
        }}
      >
        {groups.map((group, gi) => {
          const totals = subGroupTotals(group);
          const delta = subGroupDelta(group, item);
          const isOpen = openGroup?.uid === group.uid;
          const single = group.items.length === 1;

          const orChip = (
            <Box
              sx={{
                fontSize: "10.5px",
                fontWeight: 700,
                padding: "2px 7px",
                borderRadius: "4px",
                background: v2.surface,
                color: v2.ink3,
                border: `1px solid ${v2.line}`,
                whiteSpace: "nowrap",
                flex: "none",
              }}
            >
              או
            </Box>
          );

          /*
            An alternative with no stored name shows the NAME OF ITS FOOD, not an empty box.

            Once the one-food layout put this field where the food's own name used to be printed,
            an unnamed group had nothing left to show — a row of blank inputs where the swaps
            should be. Groups the panel creates are named after their food (`autoName`), so this
            only bites the ones that arrived another way: migrated rows, and anything written
            before that naming existed.

            Shown, not stored: writing the fallback into the group would make every such group
            look hand-named, and `withItems` would then stop keeping the name in step with the
            foods.

            ── and it is not an input until it is asked to be ──

            The name USED to be a permanently open text field. On a one-food alternative that field
            sits exactly where the food's own name is printed and holds exactly the food's own name,
            so the whole thing read as "this is where you rename דג דניס לפני בישול" — an offer the
            builder does not make anywhere else, because a food's name belongs to the catalogue.

            So the name is text, and renaming is a thing you ask for. What is being renamed is then
            unmistakable: the button says חלופה, and the field only exists after you press it.
          */
          const renaming = renameGroup === group.uid;

          const nameEdit = renaming ? (
            <InlineEdit
              size="sm"
              autoFocus
              ariaLabel={`שם חלופה ${gi + 1}`}
              value={group.name || autoName(group.items)}
              onChange={(name) => updateGroup(group.uid, { name })}
              onBlur={() => setRenameGroup(null)}
              sx={{ flex: 1, minWidth: "80px" }}
            />
          ) : (
            <Box
              sx={{
                fontSize: "13px",
                fontWeight: 600,
                color: v2.ink,
                flex: 1,
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {group.name || autoName(group.items)}
            </Box>
          );

          /*
            Deliberately an icon and not a word: it sits in a row that already carries "+ הוסף מוצר",
            "סגור" and a delete, and a fourth label would crowd out the food. `title` and the label
            both name the alternative, so neither the tooltip nor a screen reader can leave a trainer
            wondering which of the two names on this row it is about.
          */
          const renameButton = renaming ? null : (
            <TextButton
              quiet
              onClick={(e) => {
                e.stopPropagation();
                setRenameGroup(group.uid);
              }}
              title={`שנה שם לחלופה ${group.name || autoName(group.items) || gi + 1}`}
              aria-label={`שנה שם לחלופה ${group.name || autoName(group.items) || gi + 1}`}
              sx={{ height: { xs: "36px", md: "26px" }, padding: "0 7px", flex: "none" }}
            >
              <RenameIcon sx={{ fontSize: 15 }} />
            </TextButton>
          );

          /*
            The secondary path, spelled out rather than hidden behind knowing that the group is
            pressable — the collapse affordances on this screen have already proven too quiet.

            It SAYS which way it goes. Once the field is open, "+ הוסף מוצר" is an offer that has
            already been taken, and a button that does the opposite of what it reads is worse than
            no button: the only way out of the field was to click some other group.

            `stopPropagation` is what makes closing actually work. The group card handles clicks
            too — that is the other way to select it — so without this the close set the open
            group to null and the bubbling click immediately set it back.
          */
          const addButton = (
            <TextButton
              onClick={(e) => {
                e.stopPropagation();
                setOpenGroup((cur) => (cur?.uid === group.uid ? null : { uid: group.uid, focus: true }));
              }}
              aria-expanded={isOpen}
              aria-label={
                isOpen
                  ? `סגור הוספת מוצר לחלופה ${group.name || gi + 1}`
                  : `הוסף מוצר לחלופה ${group.name || gi + 1}`
              }
              sx={{ height: { xs: "36px", md: "26px" }, padding: "0 9px", fontSize: "11.5px", flex: "none" }}
            >
              {isOpen ? "סגור" : "+ הוסף מוצר"}
            </TextButton>
          );

          /**
           * What this alternative comes to, and how far that is from the food it replaces.
           *
           * `inline` puts it beside the macros of a one-food alternative; otherwise it is the
           * band under a group of several. Same text either way — a trainer comparing swaps is
           * reading the same sentence, wherever it sits.
           */
          const deltaLine = (inline: boolean) => (
            <Box
              data-testid="substitution-delta"
              sx={{
                fontSize: "11.5px",
                fontWeight: 500,
                color: delta.withinTolerance ? v2.ink3 : v2.warn,
                ...(inline
                  ? {
                      padding: "1px 7px",
                      borderRadius: "5px",
                      whiteSpace: "nowrap",
                      background: delta.withinTolerance ? "transparent" : v2.warnWash,
                    }
                  : {
                      padding: "7px 12px",
                      background: delta.withinTolerance ? v2.canvas : v2.warnWash,
                    }),
                ...nums,
              }}
            >
              סה״כ חלופה: {energyText(totals.calories, mode, { short: true })} · מול המקורי:{" "}
              {signed(delta.calories, (v) => energyFigure(v, mode, { short: true }))} ·{" "}
              {signed(delta.protein, (v) => macroFigure("protein", v, mode))} חלבון
            </Box>
          );

          /**
           * Make this alternative the food the meal actually prescribes, and demote the current
           * one into its place. "הפוך לראשי".
           *
           * It reads as a swap rather than a promotion, and the label says the half the trainer is
           * looking at: they clicked on the row they want, and what happens to the other one is the
           * consequence, not the instruction. The tooltip spells the consequence out, because
           * "make primary" alone does not say that the current food SURVIVES — and a trainer who
           * thinks the primary is about to be discarded will not press it.
           *
           * Rendered per FOOD, not per group. A group can hold several ("eat both of these
           * instead"), and which of them should take the meal's slot is a question only the trainer
           * can answer — so each one is offered, and the demoted food takes the seat of whichever
           * is chosen. See `promoteAlternative`.
           */
          const promoteButton = (sub: FoodItemV2) =>
            onPromote ? (
              <Tooltip
                title={`${sub.name} יהפוך למאכל של הארוחה, ו${item.name} יהפוך לחלופה`}
                arrow
                placement="top"
              >
                <span>
                  <TextButton
                    onClick={(e: React.MouseEvent) => {
                      // the group is selectable by clicking it, and this button sits inside one
                      e.stopPropagation();
                      onPromote(group.uid, sub.uid);
                    }}
                    quiet
                    aria-label={`הפוך את ${sub.name} למאכל הראשי במקום ${item.name}`}
                    sx={{
                      height: { xs: "36px", md: "26px" },
                      padding: "0 9px",
                      fontSize: "11.5px",
                      flex: "none",
                    }}
                  >
                    הפוך לראשי
                  </TextButton>
                </span>
              </Tooltip>
            ) : null;

          const foodRow = (
            sub: FoodItemV2,
            extra: Partial<React.ComponentProps<typeof FoodRowCore>> = {}
          ) => (
            <FoodRowCore
              key={sub.uid}
              item={sub}
              compact
              // Typing a quantity PINS it: the trainer has overruled the calorie match, and
              // nothing recomputes it afterwards — not a serving change on the parent, not
              // anything. It is the one number on this panel they set by hand.
              onQuantityChange={(quantity) =>
                updateGroupItems(
                  group.uid,
                  group.items.map((s) => (s.uid === sub.uid ? { ...s, quantity, quantityPinned: true } : s))
                )
              }
              // Changing the PORTION is a request to recompute, so it clears the pin and
              // re-matches: "make it 2 slices instead of 100g" means "and work out how many".
              onServingChange={(servingSizeId) =>
                updateGroupItems(
                  group.uid,
                  group.items.map((s) =>
                    s.uid === sub.uid
                      ? matchedToCalories({ ...withServing(s, servingSizeId), quantityPinned: false }, item)
                      : s
                  )
                )
              }
              onDelete={() => updateGroupItems(group.uid, group.items.filter((s) => s.uid !== sub.uid))}
              {...extra}
            />
          );

          return (
            <Box
              key={group.uid}
              data-testid="substitution-group"
              // pressing the alternative selects it — that is what reveals the field for
              // adding a second food into it. Never a toggle: a click that lands on a control
              // inside the group must not close the group out from under it.
              onClick={() =>
                setOpenGroup((cur) => (cur?.uid === group.uid ? cur : { uid: group.uid, focus: false }))
              }
              /*
                An alternative must not look like a food that is IN the meal — trainers were
                reading these rows as part of the meal itself. Three things separate them, and
                it takes all three:

                  · the card is TINTED, never white. White is what a real food row is made of,
                    and a white card inside a grey panel reads as MORE solid than the row above
                    it, which is the opposite of the truth — these are not counted at all.
                  · a DASHED border, the same "provisional" mark the panel already used.
                  · the rows inside run `compact`: smaller, greyer type and shorter controls,
                    with no repeated "כמות" / "יחידת מדידה" captions.

                Selecting a group moves the border to the brand colour rather than turning the
                card white, so "this is the one I am editing" never costs the distinction.
              */
              sx={{
                border: `1px dashed ${isOpen ? v2.brand : "#C7D2DC"}`,
                borderRadius: "9px",
                background: isOpen ? "#E9EFF4" : "#EEF2F6",
                overflow: "hidden",
                transition: "background .12s, border-color .12s",
              }}
            >
              {/*
                ONE LINE per alternative, whenever it holds one food — which is nearly all of
                them. It used to take three stacked bands: a header carrying the group's name, the
                food row, and the delta. The header's name was the FOOD's name, printed a second
                time (auto-generated from it), the delta repeated the calories the row had just
                shown, and a swap for one banana stood as tall as the meal it hangs off.

                So the parts move INTO the row: the "או" chip and the still-editable group name
                take the row's name slot, the delta sits beside the macros, and "+ הוסף מוצר"
                joins the row's own action cluster. The row's trash deletes the whole group,
                because with one food inside, deleting the food and deleting the alternative are
                the same act — two separate deletes for one outcome is how you get the wrong one.

                A group holding SEVERAL foods keeps the stacked layout: it has a name of its own
                that is not any one food's, and a total that is not any one row's.
              */}
              {single ? (
                foodRow(group.items[0], {
                  nameSlot: (
                    <Box sx={{ display: "flex", alignItems: "center", gap: "7px", flex: 1, minWidth: 0 }}>
                      {orChip}
                      {nameEdit}
                      {renameButton}
                    </Box>
                  ),
                  note: deltaLine(true),
                  actions: (
                    <>
                      {promoteButton(group.items[0])}
                      {addButton}
                    </>
                  ),
                  onDelete: () => removeGroup(group.uid),
                })
              ) : (
                <>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: "8px",
                      padding: "8px 10px",
                      background: "transparent",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flex: 1, minWidth: 0 }}>
                      {orChip}
                      {nameEdit}
                      {renameButton}
                    </Box>
                    {addButton}
                    <TextButton
                      tone="danger"
                      onClick={() => removeGroup(group.uid)}
                      quiet
                      sx={{ height: { xs: "36px", md: "26px" }, padding: "0 9px", fontSize: "11.5px", flex: "none" }}
                    >
                      מחק
                    </TextButton>
                  </Box>

                  {group.items.map((sub) => foodRow(sub, { actions: promoteButton(sub) }))}

                  {group.items.length > 0 && deltaLine(false)}
                </>
              )}

              {/* Only on the selected group. A field on every group is what made stacking two
                  foods into one alternative easier than creating two alternatives. */}
              {isOpen && (
                <FoodSearchField
                  placeholder="הוסף מוצר לחלופה זו..."
                  autoFocus={openGroup?.focus}
                  onAdd={(newItem) =>
                    updateGroupItems(group.uid, [
                      ...group.items,
                      matchedToCalories({ ...newItem, order: group.items.length }, item),
                    ])
                  }
                />
              )}
            </Box>
          );
        })}
      </Box>

      {/*
        The primary action, and it sits BELOW the alternatives rather than above them.

        Adding is an append: the group a search creates lands at the end of the list, so a field
        at the top put the control at the opposite end of the panel from the row it produced —
        you typed at the top and the answer appeared at the bottom, past everything already
        there. Underneath, the field is where the next alternative is about to appear and the
        list grows down towards it, which is also how the meal's own food search behaves one
        level up.

        The white card around the field keeps it reading as THE control here rather than as one
        more dashed group.
      */}
      <Box
        sx={{
          margin: { xs: "8px 22px 0 4px", md: "10px 34px 0 4px" },
          border: `1px solid ${v2.line}`,
          borderRadius: "9px",
          background: v2.surface,
          overflow: "hidden",
        }}
      >
        <FoodSearchField placeholder="חפש מוצר והוסף כחלופה..." onAdd={addGroupFromFood} />
      </Box>

      {/*
        The way out, at the END of the panel.

        There has always been one, on the food's own row — the "3 חלופות" badge and the icon
        beside it both toggle this open. Both are ABOVE it, and a food with several alternatives
        fills a screen: the trainer finishes reading the last swap and the only control that
        closes it is off the top, so they scroll back up past everything they just read to press
        it. A second one costs a line and saves that trip.

        It only SAYS close. Unlike the toggles on the row, which have to describe a state the
        trainer cannot see, this button is only ever rendered while the panel is open.
      */}
      <Box sx={{ display: "flex", alignItems: "center", gap: "10px", padding: { xs: "8px 22px 8px 4px", md: "9px 34px 9px 4px" } }}>
        <Box sx={{ fontSize: "11.5px", color: v2.ink4, flex: 1, minWidth: 0 }}>
          כל מוצר שתבחרו נוסף כחלופה נפרדת · כדי לצרף מוצר לחלופה קיימת — לחצו עליה
        </Box>
        {onClose && (
          <TextButton
            onClick={onClose}
            aria-label={`סגור חלופות ל${item.name}`}
            sx={{ height: { xs: "36px", md: "26px" }, padding: "0 10px", fontSize: "11.5px", flex: "none" }}
          >
            <CollapseIcon sx={{ fontSize: 14 }} />
            סגור חלופות
          </TextButton>
        )}
      </Box>
    </Box>
  );
};

export default SubstitutionsPanel;
